import iconSmileySticker from '@phosphor-icons/core/regular/smiley-sticker.svg';
import clsx from 'clsx';
import React, { useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Emoji from '@/components/ui/emoji';
import Icon from '@/components/ui/icon';
import EmojiPickerDropdown from '@/emoji/containers/emoji-picker-dropdown-container';
import unicodeMapping from '@/emoji/mapping';
import { useFeatures } from '@/hooks/use-features';
import { useLoggedIn } from '@/hooks/use-logged-in';
import { useLongPress } from '@/hooks/use-long-press';
import {
  useEmojiReactMutation,
  useEmojiUnreactMutation,
} from '@/queries/statuses/use-status-interactions';
import { useModalsActions } from '@/stores/modals';
import { useSettings } from '@/stores/settings';

import AnimatedNumber from '../animated-number';

import type { Emoji as EmojiType } from '@/emoji';
import type { SelectedStatus } from '@/queries/statuses/use-status';
import type { EmojiReaction } from 'pl-api';

const messages = defineMessages({
  emojiCount: {
    id: 'status.reactions.label',
    defaultMessage: '{count} {count, plural, one {person} other {people}} reacted with {emoji}',
  },
  addEmoji: { id: 'emoji_button.label', defaultMessage: 'Insert emoji' },
});

interface IStatusReactionsBar {
  status: Pick<SelectedStatus, 'id' | 'emoji_reactions'>;
  collapsed?: boolean;
}

interface IStatusReaction {
  statusId: string;
  reaction: EmojiReaction;
  obfuscate?: boolean;
  unauthenticated?: boolean;
}

const StatusReaction: React.FC<IStatusReaction> = ({
  reaction,
  statusId,
  obfuscate,
  unauthenticated,
}) => {
  const node = useRef<HTMLButtonElement>(null);
  const intl = useIntl();
  const features = useFeatures();
  const { openModal } = useModalsActions();

  const { mutate: emojiReact } = useEmojiReactMutation(statusId);
  const { mutate: emojiUnreact } = useEmojiUnreactMutation(statusId);

  const bind = useLongPress((e) => {
    if (!features.emojiReactsList || e.type !== 'touchstart') return;

    e.stopPropagation();

    if ('vibrate' in navigator) navigator.vibrate(1);
    node.current?.focus();
    openModal('REACTIONS', { statusId: statusId, reaction: reaction.name });
  });

  if (!reaction.count) return null;

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation();

    if (unauthenticated) {
      if (!features.emojiReactsList) return;
      openModal('REACTIONS', { statusId, reaction: reaction.name });
    } else if (reaction.me) {
      emojiUnreact(reaction.name);
    } else {
      emojiReact(reaction.name);
    }
  };

  let shortCode = reaction.name;

  if (unicodeMapping[shortCode]?.shortcode) {
    shortCode = unicodeMapping[shortCode].shortcode;
  }

  return (
    <button
      ref={node}
      className={clsx('status-reactions-bar__button', {
        'status-reactions-bar__button--active': reaction.me,
      })}
      onClick={handleClick}
      title={intl.formatMessage(messages.emojiCount, {
        emoji: `:${shortCode}:`,
        count: reaction.count,
      })}
      // disabled={unauthenticated}
      {...bind}
    >
      <Emoji emoji={reaction.name} src={reaction.url ?? undefined} />

      <p>
        <AnimatedNumber value={reaction.count} obfuscate={obfuscate} short />
      </p>
    </button>
  );
};

const StatusReactionsBar: React.FC<IStatusReactionsBar> = ({ status, collapsed }) => {
  const intl = useIntl();
  const { me } = useLoggedIn();
  const { demetricator } = useSettings();
  const features = useFeatures();

  const { mutate: emojiReact } = useEmojiReactMutation(status.id);
  const { mutate: emojiUnreact } = useEmojiUnreactMutation(status.id);

  const handlePickEmoji = (emoji: EmojiType) => {
    if (
      status.emoji_reactions.find((r) => r.name === (emoji.custom ? emoji.id : emoji.native))?.me
    ) {
      emojiUnreact(emoji.custom ? emoji.id : emoji.native);
    } else {
      emojiReact(emoji.custom ? emoji.id : emoji.native);
    }
  };

  if (demetricator === 'always') return null;
  if ((demetricator === 'on' || status.emoji_reactions.length === 0) && collapsed) return null;
  if (status.emoji_reactions.length === 0 && !features.emojiReacts) return null;

  const sortedReactions = status.emoji_reactions.toSorted(
    (a, b) => (b.count ?? 0) - (a.count ?? 0),
  );

  return (
    <div className='status-reactions-bar'>
      {sortedReactions.map((reaction) =>
        reaction.count ? (
          <StatusReaction
            key={reaction.name}
            statusId={status.id}
            reaction={reaction}
            obfuscate={demetricator !== 'off'}
            unauthenticated={!me}
          />
        ) : null,
      )}
      {me && (
        <EmojiPickerDropdown onPickEmoji={handlePickEmoji}>
          <button
            className='status-reactions-bar__picker-button'
            title={intl.formatMessage(messages.addEmoji)}
          >
            <Icon src={iconSmileySticker} aria-hidden />
          </button>
        </EmojiPickerDropdown>
      )}
    </div>
  );
};

export { StatusReactionsBar as default };
